import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Settings2, Trash2, Plus } from "lucide-react";

export const BuMappingDialog = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [tt, setTt] = useState("");
  const [bu, setBu] = useState("");

  const load = async () => {
    try {
      const { data: d } = await api.get("/operations/bu-mappings");
      setData(d);
    } catch (e) { toast.error("Failed to load mappings"); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const save = async (taskType, buLabel) => {
    if (!taskType?.trim() || !buLabel) return toast.error("Pick a task type and a BU");
    try {
      await api.post("/operations/bu-mappings", { task_type: taskType.trim(), bu_label: buLabel });
      toast.success(`"${taskType.trim()}" → ${buLabel}`);
      setTt(""); setBu("");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove mapping "${m.task_type}" → ${m.bu_label}? Those jobs return to Unmapped (or the built-in rules).`)) return;
    try {
      await api.delete(`/operations/bu-mappings/${m.id}`);
      await load(); onChanged?.();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-9 text-xs" data-testid="wip-bu-mapping-btn"
              onClick={() => setOpen(true)}>
        <Settings2 className="w-3.5 h-3.5 mr-1.5" />BU mapping
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bu-mapping-dialog">
          <DialogHeader><DialogTitle className="text-base">AroFlo task type → Business unit mapping</DialogTitle></DialogHeader>
          {!data ? <div className="p-8 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div> : (
            <div className="space-y-4 text-sm">
              {(data.unmapped_task_types || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Currently unmapped task types (open jobs)</div>
                  <div className="space-y-1.5">
                    {data.unmapped_task_types.map((u) => (
                      <div key={u.task_type} className="flex items-center gap-2" data-testid={`unmapped-${u.task_type}`}>
                        <span className="flex-1 text-xs text-slate-700 truncate">{u.task_type} <span className="text-slate-400">({u.n} jobs)</span></span>
                        <Select onValueChange={(v) => save(u.task_type, v)}>
                          <SelectTrigger className="h-8 w-64 text-xs"><SelectValue placeholder="Assign a BU…" /></SelectTrigger>
                          <SelectContent>{(data.bu_options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Custom mappings</div>
                {(data.mappings || []).length === 0 && <div className="text-xs text-slate-400">None yet.</div>}
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                  {(data.mappings || []).map((m) => (
                    <div key={m.id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`mapping-${m.task_type}`}>
                      <span className="font-medium text-slate-800">{m.task_type}</span>
                      <span className="text-slate-400">→</span>
                      <span className="flex-1 text-slate-600">{m.bu_label}</span>
                      <button onClick={() => remove(m)} className="text-slate-300 hover:text-rose-600" data-testid={`mapping-del-${m.task_type}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Add a mapping manually</div>
                <div className="flex items-center gap-2">
                  <Input value={tt} onChange={(e) => setTt(e.target.value)} placeholder="AroFlo task type (exact text)"
                         className="h-8 text-xs flex-1" data-testid="mapping-new-tt" />
                  <Select value={bu} onValueChange={setBu}>
                    <SelectTrigger className="h-8 w-64 text-xs" data-testid="mapping-new-bu"><SelectValue placeholder="Business unit" /></SelectTrigger>
                    <SelectContent>{(data.bu_options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" className="h-8 text-xs" onClick={() => save(tt, bu)} data-testid="mapping-add-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />Add
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">Matches are case-insensitive "contains" on the AroFlo task type. Custom mappings beat the built-in rules and apply across WIP, Aged work, Top clients and CSAT.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
